import { h } from "preact";
import { useMemo } from "preact/hooks";

import PreviewBackgroundColor, { DEFAULT_PREVIEW_BACKGROUND_COLOR, isDefaultPreviewBackgroundColor } from "../../../../foundation/api/model/colors/PreviewBackgroundColor";
import RawColor from "../../../../foundation/api/model/colors/RawColor";
import { MenuButtonAsMenuItem } from "../MenuButton";
import { MenuColorSelectorAsMenuItem } from "../MenuColorSelector";
import { DividerAsMenuItem } from "../MenuDivider";
import Submenu from "../Submenu";
import withMenuItem, { WithMenuItemProps } from "../withMenuItem";

import "../style.css";

const CUSTOM_BACKGROUND_COLORS = [
    {
        name: "white", r: 255, g: 255, b: 255, a: 1
    },
    {
        name: "lightYellow", r: 255, g: 251, b: 239, a: 1
    },
    {
        name: "lightBlue", r: 235, g: 247, b: 255, a: 1
    },
    {
        name: "snowman", r: 251, g: 251, b: 251, a: 1
    },
    {
        name: "lightGray", r: 213, g: 212, b: 213, a: 1
    },
    {
        name: "gandalf", r: 113, g: 113, b: 122, a: 1
    },
    {
        name: "teflon", r: 63, g: 63, b: 70, a: 1
    }
];

interface Props extends WithMenuItemProps {
    class?: string;
    colorValue: PreviewBackgroundColor;
    colors?: RawColor[];
    mixed?: boolean;
    onChange(value: PreviewBackgroundColor): void;
}

function ColorSelectorSubmenu({
    class: cssClass,
    colorValue,
    content,
    open,
    highlighted,
    disabled,
    colors = CUSTOM_BACKGROUND_COLORS,
    mixed = false,
    indicesPath,
    onChange,
    onMenuItemMouseEnter,
    handleMenuChildMouseEnter,
    onMenuItemMouseMove,
    handleMenuChildMouseMove
}: Props) {
    const isDefaultChecked = useMemo(() =>
        isDefaultPreviewBackgroundColor(colorValue)
    , [colorValue]);

    function handleChange(newValue: PreviewBackgroundColor) {
        onChange(newValue);
    }

    function getSubmenuContent() {
        let displayValue: string;
        if (mixed) {
            displayValue = "Mixed";
        } else if (isDefaultChecked) {
            displayValue = "Default";
        } else {
            displayValue = "Custom";
        }

        return content
            ? `${content} (${displayValue})`
            : displayValue;
    }

    return (
        <Submenu
            class={cssClass}
            content={getSubmenuContent()}
            indicesPath={indicesPath}
            highlighted={highlighted}
            open={open}
            disabled={disabled}
            handleMenuChildMouseEnter={handleMenuChildMouseEnter}
            handleMenuChildMouseMove={handleMenuChildMouseMove}
            onMenuItemMouseEnter={onMenuItemMouseEnter}
            onMenuItemMouseMove={onMenuItemMouseMove}>
            <MenuButtonAsMenuItem
                checked={!mixed && isDefaultChecked}
                disabled={disabled}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => handleChange(DEFAULT_PREVIEW_BACKGROUND_COLOR)}>
                Default
            </MenuButtonAsMenuItem>
            <DividerAsMenuItem/>
            <MenuColorSelectorAsMenuItem
                colorValue={mixed ? undefined : colorValue}
                colors={colors}
                disabled={disabled}
                onChange={handleChange}/>
        </Submenu>
    );
}

const ColorSelectorSubmenuAsMenuItem = withMenuItem(ColorSelectorSubmenu);

export default ColorSelectorSubmenu;
export { ColorSelectorSubmenuAsMenuItem };
